import { DateTime } from 'luxon';
import i18n from 'i18next';

export const ConvertDateToLocale = (date: string, format: Intl.DateTimeFormatOptions) => {
  if(!date || date === "") 
    return i18n.t('ticket.ticket-board.ticket-card.none');
  
  const locale = navigator.language ?? i18n.language;
  return DateTime.fromJSDate(new Date(date)).setLocale(locale).toLocaleString(format);
}

// Languages that write a date and its time with only a space between them.
const SPACE_JOINED_LANGUAGES = ['ja', 'hu', 'tr'];

/**
 * What goes between a date and its time: a comma, or a space in the languages that use one.
 * Follows the language the date itself is written in (ConvertDateToLocale's), so the separator
 * always matches the text either side of it.
 */
export const DateTimeSeparator = () => {
  const language = (navigator.language ?? i18n.language ?? '').split('-')[0].toLowerCase();
  return SPACE_JOINED_LANGUAGES.includes(language) ? ' ' : ', ';
};

/**
 * A date and its time as one phrase - "Oct 3, 2026, 10:12 AM" - the same in every browser.
 * Asked for both in one call, iOS Safari joins them with "at" where Chrome uses a comma, so the
 * two are formatted separately and joined here.
 */
export const ConvertDateTimeToLocale = (date: string, dateFormat: Intl.DateTimeFormatOptions) => {
  if (!date || date === "")
    return i18n.t('ticket.ticket-board.ticket-card.none');

  return `${ConvertDateToLocale(date, dateFormat)}${DateTimeSeparator()}${ConvertDateToLocale(date, DateTime.TIME_SIMPLE)}`;
};

/**
 * A ticket's created date, short - "Oct 3, 2026" - and the same with its time,
 * "Oct 3, 2026, 10:12 AM". Shared by the collapsed ticket cards so the two cannot drift apart;
 * each shows the time only while there is room for it.
 */
export const ShortDateLabels = (date: string): { dateOnly: string; withTime: string } => {
  const format: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return { dateOnly: ConvertDateToLocale(date, format), withTime: ConvertDateTimeToLocale(date, format) };
};

export const ConvertISODateToLocal = (date: string, format: string) => {
  return DateTime.fromISO(date, { zone: 'utc' }).toLocal().toFormat(format, { locale: i18n.language });
};

export const IsDateThisWeek = (date: string) => {
  const queryDate = DateTime.fromJSDate(new Date(date));

  const dt = DateTime.utc();
  const currentYear = dt.year;
  const nextWeek = dt.weekNumber;
  const nextWeekDt = DateTime.fromObject({
    weekYear: currentYear,
    weekNumber: nextWeek
  });

  let isNextWeek = false;

  if (
    nextWeekDt.startOf('week').toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 1 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 2 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 3 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 4 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 5 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').minus({ day: 1 }).toISO() === queryDate.toISO()
  ) {
    isNextWeek = true;
  }

  return isNextWeek;
};

export const IsDateNextWeek = (date: string) => {
  const queryDate = DateTime.fromJSDate(new Date(date));

  const dt = DateTime.utc();
  const currentYear = dt.year;
  const nextWeek = dt.weekNumber + 1;
  const nextWeekDt = DateTime.fromObject({
    weekYear: currentYear,
    weekNumber: nextWeek
  });

  let isNextWeek = false;

  if (
    nextWeekDt.startOf('week').toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 1 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 2 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 3 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 4 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').plus({ day: 5 }).toISO() === queryDate.toISO() ||
    nextWeekDt.startOf('week').minus({ day: 1 }).toISO() === queryDate.toISO()
  ) {
    isNextWeek = true;
  }

  return isNextWeek;
};

export const IsDateFuture = (date: string) => {
  const queryDate = DateTime.fromJSDate(new Date(date));

  const dt = DateTime.utc();
  const currentYear = dt.year;
  const nextWeek = dt.weekNumber + 1;
  const nextWeekDt = DateTime.fromObject({
    weekYear: currentYear,
    weekNumber: nextWeek
  });

  let isFuture = false;

  if (nextWeekDt.startOf('week').plus({ day: 6 }).toISO() < queryDate.toISO()) {
    isFuture = true;
  }

  return isFuture;
};

export const IsDateLate = (date: string | null) => {
  const queryDate = DateTime.fromJSDate(new Date(date));
  if (date === null) {
    return false;
  }
  return DateTime.fromJSDate(new Date()).toISO() > queryDate.toISO();
};

export const IsDateToday = (date: string) => {
  const queryDate = DateTime.fromJSDate(new Date(date));
  return queryDate.hasSame(DateTime.local(), 'day');
};

export const IsDateTomorrow = (date: string) => {
  const queryDateMinus1 = DateTime.fromJSDate(new Date(date)).minus({ day: 1 });
  return queryDateMinus1.hasSame(DateTime.local(), 'day');
};
